import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Heading, LoadingSpinner, Text } from '../components/ui'
import { ClassificationWithTrend } from '../components/ClassificationTrendIndicator'
import { buildClassificationColorMap, buildClassificationRankMap } from '../utils/classificationTrend'
import { dashcorretorService } from '../services/dashcorretor'
import { portaisAtendimentoService } from '../services/portaisAtendimento'
import { logPortaisAtendimento } from '../utils/portaisAtendimentoLogs'
import { usePermission } from '../hooks/usePermissions'
import type { PortaisAtendimentoCallQueueItem } from '../types/portaisAtendimento'
import type { DashCorretorClassificationRef } from '../types/dashcorretor'
import { XMarkIcon } from '@heroicons/react/24/outline'

const MODULE_NAME = 'portais_atendimento'
const ITEMS_PER_PAGE = 20

interface PortaisAtendimentoCallQueueManageModalProps {
  open: boolean
  onClose: () => void
  onQueueChanged: () => void
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Erro inesperado. Tente novamente.'
}

const PortaisAtendimentoCallQueueManageModal: React.FC<PortaisAtendimentoCallQueueManageModalProps> = ({
  open,
  onClose,
  onQueueChanged,
}) => {
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const [items, setItems] = useState<PortaisAtendimentoCallQueueItem[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedBrokerIds, setSelectedBrokerIds] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [removeError, setRemoveError] = useState<string | null>(null)
  const [classificationRefs, setClassificationRefs] = useState<DashCorretorClassificationRef[]>([])

  const classificationRankMap = useMemo(
    () => buildClassificationRankMap(classificationRefs),
    [classificationRefs]
  )
  const classificationColorMap = useMemo(
    () => buildClassificationColorMap(classificationRefs),
    [classificationRefs]
  )

  const loadQueue = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    setSelectedBrokerIds(new Set())
    setCurrentPage(1)
    setRemoveError(null)

    try {
      const [queueItems, refs] = await Promise.all([
        portaisAtendimentoService.listCallQueueWithBrokers(),
        dashcorretorService.listClassificationReferences(),
      ])
      setItems(queueItems)
      setClassificationRefs(refs)
    } catch (error) {
      setLoadError(getErrorMessage(error))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (open) {
      void loadQueue()
    }
  }, [open, loadQueue])

  const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE))
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const pagedItems = items.slice(startIndex, endIndex)

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const toggleItem = (brokerId: string) => {
    setSelectedBrokerIds((prev) => {
      const next = new Set(prev)
      if (next.has(brokerId)) next.delete(brokerId)
      else next.add(brokerId)
      return next
    })
  }

  const toggleSelectAllOnPage = () => {
    const visibleIds = pagedItems.map((item) => item.broker_id)
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedBrokerIds.has(id))
    setSelectedBrokerIds((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id))
      } else {
        visibleIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const handleConfirmRemove = async () => {
    const idsToRemove = Array.from(selectedBrokerIds)
    if (idsToRemove.length === 0) return

    setRemoving(true)
    setRemoveError(null)

    try {
      await portaisAtendimentoService.removeBrokersFromCallQueue(idsToRemove)

      const removedItems = items.filter((item) => selectedBrokerIds.has(item.broker_id))
      await logPortaisAtendimento('delete', `Removido(s) ${idsToRemove.length} corretor(es) da fila de ligação`, {
        entityId: idsToRemove[0] ?? null,
        metadata: {
          entidade: 'helpdesk_broker_call_queue',
          removed_count: idsToRemove.length,
          broker_ids: idsToRemove,
          broker_names: removedItems.map((i) => i.broker_name),
          broker_scgp_ids: removedItems.map((i) => i.broker_id_scgp),
          reasons: removedItems.map((i) => i.reason),
        },
      })

      setConfirmRemoveOpen(false)
      setSelectedBrokerIds(new Set())
      onQueueChanged()
      await loadQueue()
    } catch (error) {
      setRemoveError(getErrorMessage(error))
    } finally {
      setRemoving(false)
    }
  }

  if (!open) return null

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={onClose}
        role="presentation"
      >
        <div
          className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-labelledby="call-queue-manage-modal-title"
          aria-modal="true"
        >
          <div className="flex items-start justify-between mb-4 gap-4">
            <div>
              <Heading as="h3" size="2xl" id="call-queue-manage-modal-title" className="text-gray-900 dark:text-white">
                Gerenciar fila de ligação
              </Heading>
              <Text size="sm" color="muted" className="mt-1">
                {items.length} {items.length === 1 ? 'corretor na fila' : 'corretores na fila'}
              </Text>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {canDelete && selectedBrokerIds.size > 0 && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-3">
              <Text size="sm" className="text-gray-700 dark:text-gray-300">
                {selectedBrokerIds.size} selecionado{selectedBrokerIds.size === 1 ? '' : 's'}
              </Text>
              <Button
                variant="solid"
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => setConfirmRemoveOpen(true)}
                disabled={removing}
              >
                Remover da fila
              </Button>
            </div>
          )}

          {removeError && (
            <div className="mb-4 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-4 py-3">
              <Text size="sm" className="text-red-700 dark:text-red-300">{removeError}</Text>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : loadError ? (
            <Text size="sm" className="text-red-600 dark:text-red-400">{loadError}</Text>
          ) : items.length === 0 ? (
            <Text size="sm" color="muted" className="py-8 text-center block">
              Nenhum corretor na fila de ligação.
            </Text>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700 min-w-[720px]">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {canDelete && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12">
                          <input
                            type="checkbox"
                            checked={
                              pagedItems.length > 0 &&
                              pagedItems.every((item) => selectedBrokerIds.has(item.broker_id))
                            }
                            onChange={toggleSelectAllOnPage}
                            aria-label="Selecionar todos da página"
                          />
                        </th>
                      )}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nome</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoria</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código SCGP</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Motivo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pagedItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        {canDelete && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedBrokerIds.has(item.broker_id)}
                              onChange={() => toggleItem(item.broker_id)}
                              aria-label={`Selecionar ${item.broker_name}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{item.broker_name}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <ClassificationWithTrend
                            classification={item.broker_classification}
                            classificationPrevious={null}
                            rankMap={classificationRankMap}
                            colorMap={classificationColorMap}
                            emptyLabel="—"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {item.broker_id_scgp ?? '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{item.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <Text size="sm" color="muted">
                    Mostrando {startIndex + 1} a {Math.min(endIndex, items.length)} de {items.length}
                  </Text>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>
                    <Text size="sm" color="muted">
                      Página {currentPage} de {totalPages}
                    </Text>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Próximo
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="mt-6 flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      </div>

      {confirmRemoveOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <Heading as="h3" size="2xl" className="text-red-600 dark:text-red-400 mb-4">
              Remover da fila
            </Heading>
            <Text size="sm" className="font-medium text-gray-900 dark:text-white mb-1">
              Tem certeza que deseja remover {selectedBrokerIds.size}{' '}
              {selectedBrokerIds.size === 1 ? 'corretor' : 'corretores'} da fila de ligação?
            </Text>
            <Text size="sm" color="muted" className="mb-6">
              Esta ação não remove o corretor do cadastro, apenas da fila manual de ligação.
            </Text>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmRemoveOpen(false)} disabled={removing}>
                Cancelar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={removing}
                onClick={() => void handleConfirmRemove()}
              >
                {removing ? 'Removendo...' : 'Remover'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PortaisAtendimentoCallQueueManageModal
